/* eslint-disable no-unused-vars */
import _ from 'underscore';
import { useCallback, useContext } from 'react';
import { useCreateHookResultPromise } from '@bingads-webui-campaign-react/common-hooks';
import { useSuggestionsContext } from '@bingads-webui-campaign-react/pmax-suggestion-service';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { edmCampaign } from '@bingads-webui/edm-campaign';
import {
  useSearchLocationData,
  usePreference,
  preferenceNames,
  useAudienceData,
} from '@bingads-webui-campaign-react/pmax-lite-data';
import { LocationTarget } from '@bingads-webui-react/bing-maps';
import { targetOptions } from '@bingads-webui-campaign-react/location-targeting';
import { readAssetGroup, createOrUpdateAssetGroup } from '@bingads-webui-campaign-react/perf-max-data';
import { isSDK } from '@bingads-webui-campaign-react/sdk-utils';
import { usePilotsValue } from '@bingads-webui-campaign-react/basic-context-utils';

import { useTargetInstrument } from './use-target-instrument';
import { useTargetAdapter } from './use-target-adapter';
import { buildLocationTargetingData, shouldSelectQuickOption } from './utils/common';
import { formatAudienceListToFlatTree, modelToViewModelForAudienceGroup, dimensionFormatter } from './utils/audience-signal';
import { formatSuggestedLocation } from './utils/location-targets';
import { viewModelToBulkModelForSearchThemes } from './utils/search-themes';

export const createOrUpdateAudienceGroup = (data) => {
  const audienceGroupEDM = edmCampaign.types.resolve('Model.AudienceGroup');
  const audienceGroupResource = new audienceGroupEDM.ResourceIdentifier();
  const createOrUpdateCall = data && data.Id > 0 ?
    audienceGroupResource.bulkUpdate.bind(audienceGroupResource) :
    audienceGroupResource.bulkCreate.bind(audienceGroupResource);

  return createOrUpdateCall(data);
};

export const useTargetingRepository = () => {
  const { scope } = useContext(ScopeContext);
  const isAudienceSignalEnabled = !isSDK();
  const isSearchThemesEnabled = !isSDK();

  const { locationLogger, audienceSignalLogger } = useTargetInstrument();
  const {
    cid, aid, campaignId, assetGroupId,
  } = scope.values;

  const {
    isTargetsDone,
    targets: preLoadedTargetsSuggestions,
  } = useSuggestionsContext();

  const [, getAudienceData] = useAudienceData({ postponeFetch: true });

  const createSuggestionPromise = useCreateHookResultPromise({
    result: isTargetsDone && (preLoadedTargetsSuggestions || []),
  });

  const [, { readTargets, writeLocationTargetChanges }] = useTargetAdapter(locationLogger);
  const { getLocation } = useSearchLocationData(aid, cid);

  const [{ value: countryPreference }] = usePreference(preferenceNames.SelectedCountry);

  const loadQuickOptionCountry = useCallback(() => getLocation(countryPreference).then((result) => {
    locationLogger.trace(`Load quick option location ${countryPreference}: ${JSON.stringify(result)}`);

    const bundleCountries = _.filter(result.value, item => item.Type === 'Country' && item.CountryCode === countryPreference);

    return bundleCountries[0];
  }).catch((error) => {
    locationLogger.error(`Load quick option location ${countryPreference} failed: ${JSON.stringify(error)}`);
    return Promise.reject(error);
  }), [countryPreference, getLocation, locationLogger]);

  const loadAssetGroup = useCallback(
    () => readAssetGroup(cid, aid, campaignId, assetGroupId, isSearchThemesEnabled)
      .then((assetGroup) => {
        locationLogger.trace(`Load website URL: ${JSON.stringify(assetGroup)}`);

        return {
          website: _.first(assetGroup.FinalUrls),
          audienceGroup: assetGroup.AudienceGroup || {},
          searchThemes: assetGroup.AssetGroupSearchThemes || [],
        };
      }).catch((error) => {
        locationLogger.error(`Load website URL Failed: ${JSON.stringify(error)}`);

        return Promise.reject(error);
      })
    , [aid, assetGroupId, campaignId, cid, locationLogger, isSearchThemesEnabled]
  );

  const loadAudienceData = useCallback(() => {
    if (isAudienceSignalEnabled) {
      return getAudienceData().then((audienceList) => {
        audienceSignalLogger.trace(`Load audience data: ${JSON.stringify(audienceList)}`);

        return audienceList;
      }).catch((error) => {
        audienceSignalLogger.error(`Load audience data failed: ${JSON.stringify(error)}`);
        return Promise.reject(error);
      });
    }

    return new Promise(resolve => resolve([]));
  }, [isAudienceSignalEnabled, getAudienceData, audienceSignalLogger]);

  const load = useCallback(() => {
    const initialRequests = [
      loadQuickOptionCountry(),
      readTargets(), // Load user saved targets
      createSuggestionPromise(), // Load suggested targets
      loadAssetGroup(),
      loadAudienceData(), // Load audience data
    ];

    return Promise.all(initialRequests).then((res) => {
      const [quickOptionCountry, savedTargets, targetsSuggestion, assetGroup, audienceList] = res;
      const { website, audienceGroup, searchThemes } = assetGroup;

      const formattedSuggestedTargets = _.map(targetsSuggestion, item => formatSuggestedLocation(item));
      const suggestedLocationTargets = buildLocationTargetingData({
        positiveLocationTargets: formattedSuggestedTargets,
      });

      const shouldCheckQuickOption = shouldSelectQuickOption(
        quickOptionCountry,
        savedTargets,
        targetsSuggestion
      );
      const targets = _.isEmpty(savedTargets) ? suggestedLocationTargets : savedTargets;
      const quickOptionLocationTarget = LocationTarget.CreateIncludedLocationTarget(quickOptionCountry);
      const [audienceListInFlatTree, valueToAudience, valueToFlatItem] = formatAudienceListToFlatTree(audienceList);

      return {
        targets: {
          ...targets,
          selectedLocationOption: shouldCheckQuickOption ? targetOptions.quick : targets.selectedLocationOption,
          positiveLocationTargets: shouldCheckQuickOption
            ? [quickOptionLocationTarget]
            : targets.positiveLocationTargets,
        },
        quickOptionCountry: quickOptionLocationTarget,
        website,
        audiencesSignal: {
          audienceGroupId: audienceGroup.Id,
          audienceGroupName: audienceGroup.Name,
          valueToAudience,
          valueToFlatItem,
          audienceListInFlatTree,
          ...modelToViewModelForAudienceGroup(audienceGroup),
        },
        searchThemes,
      };
    });
  }, [createSuggestionPromise, loadAssetGroup, loadAudienceData, loadQuickOptionCountry, readTargets]);

  const save = ({ data }) => {
    const { targets = {}, audiencesSignal = {}, searchThemes = {} } = data || {};
    const {
      positiveLocationTargets,
      negativeLocationTargets,
      isPeopleInLocationChecked,
      isPeopleSearchingForLocationChecked,
      selectedLocationOption,
      locationError,
    } = targets;
    const {
      interests,
      ageRanges,
      genderTypes,
      valueToAudience,
      audienceGroupId,
      audienceGroupName,
    } = audiencesSignal;

    if (locationError) {
      return Promise.reject(locationError);
    }

    const audienceData = {
      AgeRanges: dimensionFormatter.ageRanges(ageRanges),
      GenderTypes: dimensionFormatter.genderTypes(genderTypes),
      Audiences: interests.map(interest => valueToAudience[interest]),
      Id: audienceGroupId || -100,
      Name: audienceGroupName || `Default-${Date.now()}`,
    };

    const updateAudience = () => {
      if (isAudienceSignalEnabled) {
        return createOrUpdateAudienceGroup(audienceData).then((result) => {
          const patchData = {
            AudienceGroupAssetGroupAssociations: [{
              AudienceGroupId: result.Id,
              AssetGroupId: assetGroupId,
              Status: 'Active',
            }],
            Id: assetGroupId,
            Campaign: { Id: campaignId },
          };

          return patchData;
        });
      }

      return new Promise(resolve => resolve({}));
    };

    const updateAssetGroup = () => {
      if (isSearchThemesEnabled) {
        return updateAudience().then((audiencePatchData) => {
          const patchData = {
            ...(!_.isEmpty(audiencePatchData) ? audiencePatchData : {}),
            ...(!_.isEmpty(searchThemes) ? viewModelToBulkModelForSearchThemes(searchThemes, assetGroupId) : {}),
          };

          if (_.isEmpty(patchData)) {
            return new Promise(resolve => resolve({}));
          }

          return createOrUpdateAssetGroup(patchData);
        });
      }

      return updateAudience().then((audiencePatchData) => {
        if (_.isEmpty(audiencePatchData)) {
          return new Promise(resolve => resolve({}));
        }

        return createOrUpdateAssetGroup(audiencePatchData);
      });
    };

    const updateLocations = () => writeLocationTargetChanges({
      positiveLocationTargets,
      negativeLocationTargets,
      isPeopleInLocationChecked,
      isPeopleSearchingForLocationChecked,
      selectedLocationOption,
    });

    const saveRequests = [
      updateAssetGroup(),
      updateLocations(),
    ];

    return Promise.all(saveRequests);
  };

  return {
    load,
    save,
  };
};
